import { useEffect, useRef, useState } from "react";

export type Resource<T, E = unknown> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; error: E };

export interface UseResourceOptions {
  /** Keep showing the last answer while a refetch is in flight. */
  keepOnRefetch?: boolean;
}

interface Slot<T> {
  deps: readonly unknown[];
  nonce: number;
  resource: Resource<T>;
}

const LOADING = { status: "loading" } as const;

function sameDeps(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((value, i) => Object.is(value, b[i]));
}

/**
 * Loads what `deps` identify and reloads when they change. The slot records
 * the deps and refetch count its resource answers, and a render that sees
 * different ones resets it during that render, so a stale answer is never
 * committed under new deps. The loader is read through a ref so callers need
 * not memoize it; `deps` alone decides when it runs again.
 */
export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options: UseResourceOptions = {},
): { resource: Resource<T>; refetch: () => void } {
  const [nonce, setNonce] = useState(0);
  const [slot, setSlot] = useState<Slot<T>>({
    deps,
    nonce,
    resource: LOADING,
  });

  let current = slot;
  if (slot.nonce !== nonce || !sameDeps(slot.deps, deps)) {
    const keep = options.keepOnRefetch === true && sameDeps(slot.deps, deps);
    current = { deps, nonce, resource: keep ? slot.resource : LOADING };
    setSlot(current);
  }

  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    new Promise<T>((resolve) => {
      resolve(loadRef.current(controller.signal));
    }).then(
      (data) => {
        if (!ignore) {
          setSlot((prev) => ({ ...prev, resource: { status: "ready", data } }));
        }
      },
      (error: unknown) => {
        if (!ignore) {
          setSlot((prev) => ({
            ...prev,
            resource: { status: "error", error },
          }));
        }
      },
    );
    return () => {
      ignore = true;
      controller.abort();
    };
    // `deps` is the identity of what is loaded and `nonce` the refetch count.
    // oxlint-disable-next-line react/exhaustive-deps -- the list is caller-supplied by design
  }, [...deps, nonce]);

  return {
    resource: current.resource,
    refetch: () => {
      setNonce((n) => n + 1);
    },
  };
}
